import { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import {
  BlockNoteSchema,
  defaultInlineContentSpecs,
} from '@blocknote/core';
import { useCreateBlockNote, createReactInlineContentSpec } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/ariakit';
import '@blocknote/core/style.css';
import '@blocknote/ariakit/style.css';

import { uploadDocumentImage } from '../../services/documentService';
import { useAuthStore } from '../../store/authStore';

// ─── 사용자 멘션 Inline Content ───
const UserMention = createReactInlineContentSpec(
  {
    type: 'userMention' as const,
    propSchema: {
      userId: { default: '' },
      userName: { default: '' },
    },
    content: 'none',
  },
  {
    render: (props) => (
      <span
        className="bg-blue-50 text-blue-700 px-1 rounded font-medium"
        data-type="user"
      >
        @{props.inlineContent.props.userName}
      </span>
    ),
  },
);

// ─── 이슈 멘션 Inline Content ───
const IssueMention = createReactInlineContentSpec(
  {
    type: 'issueMention' as const,
    propSchema: {
      issueId: { default: '' },
      issueKey: { default: '' },
    },
    content: 'none',
  },
  {
    render: (props) => (
      <span
        className="bg-purple-50 text-purple-700 px-1 rounded font-medium cursor-pointer hover:underline"
        data-type="issue"
        onClick={() => {
          const key = props.inlineContent.props.issueKey;
          const projectKey = key.split('-')[0];
          window.open(`/projects/${projectKey}/issues?search=${encodeURIComponent(key)}`, '_blank');
        }}
      >
        #{props.inlineContent.props.issueKey}
      </span>
    ),
  },
);

// ─── Schema ───
const schema = BlockNoteSchema.create({
  inlineContentSpecs: {
    ...defaultInlineContentSpecs,
    userMention: UserMention,
    issueMention: IssueMention,
  },
});

const CURSOR_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
  '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
];

function getUserColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = ((hash << 5) - hash + userId.charCodeAt(i)) | 0;
  }
  return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

// ─── Props ───
interface DocumentEditorProps {
  initialContent?: any[];
  onContentChange?: (blocks: any[]) => void;
  editable?: boolean;
  spaceId: string;
  collaboration?: {
    documentId: string;
    wsUrl: string;
  };
  onAwarenessChange?: (users: CollaborationUser[]) => void;
  /** 서버가 LIVE 문서를 종료했을 때 호출 (close code 4010) */
  onLiveEnded?: () => void;
  /**
   * DB에 yjsState가 저장돼 있으면 true.
   * 서버 ydoc이 빈 상태로 보이더라도 initialContent로 덮어쓰는 것을 방지한다.
   * persist 실패 등으로 서버 ydoc이 비어 보일 때 stale HTTP 스냅샷으로 덮어쓰는 데이터 유실을 막음.
   */
  hasYjsContent?: boolean;
}

export interface CollaborationUser {
  clientId: number;
  name: string;
  color: string;
}

export default function DocumentEditor({
  initialContent,
  onContentChange,
  editable = true,
  spaceId,
  collaboration,
  onAwarenessChange,
  onLiveEnded,
  hasYjsContent = false,
}: DocumentEditorProps) {
  const currentUser = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const initialContentApplied = useRef(false);
  const [collabReady, setCollabReady] = useState(!collaboration);
  const [yjsState, setYjsState] = useState<{ ydoc: Y.Doc; provider: WebsocketProvider } | null>(null);

  // ─── 콜백을 ref로 저장 (렌더 중 호출 방지 + 최신 값 보장) ───
  const onContentChangeRef = useRef(onContentChange);
  onContentChangeRef.current = onContentChange;
  const onAwarenessChangeRef = useRef(onAwarenessChange);
  onAwarenessChangeRef.current = onAwarenessChange;
  const onLiveEndedRef = useRef(onLiveEnded);
  onLiveEndedRef.current = onLiveEnded;

  // ─── Yjs doc + provider 생성/정리 ───
  useEffect(() => {
    if (!collaboration || !accessToken) {
      setYjsState(null);
      setCollabReady(true);
      return;
    }

    initialContentApplied.current = false;
    setCollabReady(false);

    const wsServerUrl = `${collaboration.wsUrl}/yjs`;
    const ydoc = new Y.Doc();
    const provider = new WebsocketProvider(
      wsServerUrl,
      collaboration.documentId,
      ydoc,
      {
        connect: true,
        params: { token: accessToken },
        disableBc: true,
      },
    );

    // ─── WebSocket 종료 감지: 서버가 LIVE 종료 시 close code 4010 전송 ───
    const handleWsClose = (event: CloseEvent) => {
      if (event.code === 4010) {
        // 서버가 LIVE 문서를 종료함 — 재연결 방지 + 부모에 알림
        provider.disconnect();
        const cb = onLiveEndedRef.current;
        if (cb) cb();
      }
    };
    // y-websocket의 내부 WebSocket에 접근해서 close 이벤트 감지
    // provider가 재연결할 때마다 새 WebSocket이 생성되므로 주기적으로 패치
    let currentWs: WebSocket | null = null;
    const patchWs = () => {
      const ws = (provider as any).ws as WebSocket | null;
      if (ws && ws !== currentWs) {
        currentWs = ws;
        ws.addEventListener('close', handleWsClose);
      }
    };
    patchWs();
    provider.on('status', patchWs);

    setYjsState({ ydoc, provider });

    return () => {
      provider.off('status', patchWs);
      provider.disconnect();
      provider.destroy();
      ydoc.destroy();
      setYjsState(null);
    };
  }, [collaboration?.documentId, collaboration?.wsUrl, accessToken]);

  // ─── Awareness 변경 감지 (ref 콜백으로 렌더 루프 방지) ───
  useEffect(() => {
    if (!yjsState) return;

    const awareness = yjsState.provider.awareness;
    const handleChange = () => {
      const cb = onAwarenessChangeRef.current;
      if (!cb) return;
      const users: CollaborationUser[] = [];
      const localClientId = awareness.clientID;
      awareness.getStates().forEach((state, clientId) => {
        if (state.user && clientId !== localClientId) {
          users.push({
            clientId,
            name: state.user.name,
            color: state.user.color,
          });
        }
      });
      // queueMicrotask로 렌더 사이클 밖에서 상태 업데이트
      queueMicrotask(() => cb(users));
    };

    awareness.on('change', handleChange);
    handleChange();

    return () => {
      awareness.off('change', handleChange);
      queueMicrotask(() => {
        const cb = onAwarenessChangeRef.current;
        if (cb) cb([]);
      });
    };
  }, [yjsState]); // onAwarenessChange를 deps에서 제거 — ref로 참조

  // ─── 에디터 생성 ───
  const editorOptions = useMemo(() => {
    const opts: any = {
      schema,
      uploadFile: async (file: File) => {
        const result = await uploadDocumentImage(spaceId, file);
        return result.url;
      },
      // URL 붙여넣기 시 자동으로 link mark 적용 (BUG-BN-001)
      pasteHandler: ({ event, editor: e, defaultPasteHandler }: any) => {
        const text = (event.clipboardData?.getData('text/plain') ?? '').trim();
        if (/^https?:\/\/[^\s]+$/.test(text)) {
          const selected = e.getSelectedText();
          if (selected) {
            // 선택 텍스트 → URL을 링크로 적용
            e.createLink(text);
          } else {
            // 커서만 있음 → URL 자체를 링크 텍스트로 삽입
            e.createLink(text, text);
          }
          return true;
        }
        return defaultPasteHandler();
      },
    };

    if (yjsState) {
      opts.collaboration = {
        fragment: yjsState.ydoc.getXmlFragment('document-store'),
        user: {
          name: currentUser?.name ?? '익명',
          color: getUserColor(currentUser?.id ?? 'anon'),
        },
        provider: yjsState.provider,
        showCursorLabels: 'activity' as const,
      };
    } else {
      opts.initialContent = initialContent && initialContent.length > 0 ? initialContent : undefined;
    }

    return opts;
  }, [yjsState, spaceId, currentUser]);

  // ★ 핵심 수정: yjsState 변경 시 에디터 재생성 (collaboration 옵션 적용)
  const editor = useCreateBlockNote(editorOptions, [collaboration?.documentId, !!yjsState]);

  // ─── LIVE 모드: Yjs 동기화 후 빈 문서면 기존 content 삽입 + 로딩 해제 ───
  // ★ 판단 기준: Y.Doc의 state vector에 다른 클라이언트 ID가 있으면 → 이미 content가 존재
  //   다른 클라이언트 ID가 없으면 → 첫 번째 클라이언트이므로 DB content로 시드
  useEffect(() => {
    if (!yjsState || !editor) return;

    const applyAndReady = () => {
      if (initialContentApplied.current) return;
      initialContentApplied.current = true;

      // Y.Doc state vector로 원격 데이터 존재 여부 판단
      // 다른 클라이언트 ID가 있으면 = 서버에서 content를 받은 것 (DB 저장분 또는 다른 유저)
      const sv = Y.decodeStateVector(Y.encodeStateVector(yjsState.ydoc));
      const localId = yjsState.ydoc.clientID;
      const hasRemoteState = Array.from(sv.keys()).some((id) => id !== localId);

      if (hasRemoteState) {
        // 원격 데이터가 있으면 replaceBlocks 하지 않음 — 이미 content가 동기화됨
        setCollabReady(true);
        return;
      }

      // DB에 yjsState가 있는데 서버 ydoc이 비어 보이는 경우:
      // persist 실패 또는 race 조건. stale HTTP 스냅샷(initialContent)으로 덮어쓰면
      // 가장 최근 Yjs 편집 내용이 유실됨 — 시드를 건너뛴다.
      if (hasYjsContent) {
        console.error('[DocumentEditor] Server ydoc appears empty but DB has yjsState. Skipping initialContent seed to prevent data loss.');
        setCollabReady(true);
        return;
      }

      // 원격 데이터 없음 + DB에도 yjsState 없음 = 진정한 첫 번째 LIVE 클라이언트
      // DB에서 가져온 initialContent로 시드
      if (initialContent && initialContent.length > 0) {
        try {
          editor.replaceBlocks(editor.document, initialContent);
        } catch (err) {
          console.warn('[DocumentEditor] Failed to apply initial content:', err);
        }
      }
      setCollabReady(true);
    };

    const handleSync = (isSynced: boolean) => {
      if (isSynced) {
        // 동기화 완료 후 약간의 딜레이 — Y.Doc 변경이 에디터에 반영될 시간 확보
        setTimeout(applyAndReady, 100);
      }
    };

    if (yjsState.provider.synced) {
      setTimeout(applyAndReady, 100);
    }
    yjsState.provider.on('sync', handleSync);

    // fallback: 3초 후 — content 삽입 없이 로딩만 해제 (입력 가능하게)
    const fallbackTimer = setTimeout(() => {
      if (!initialContentApplied.current) {
        initialContentApplied.current = true;
      }
      setCollabReady(true);
    }, 3000);

    return () => {
      yjsState.provider.off('sync', handleSync);
      clearTimeout(fallbackTimer);
    };
  }, [yjsState, editor, initialContent]);

  // ─── onChange 핸들러 — queueMicrotask로 렌더 중 부모 상태 업데이트 방지 ───
  const handleEditorChange = useCallback(() => {
    const cb = onContentChangeRef.current;
    if (!cb || !editor) return;
    const doc = editor.document;
    queueMicrotask(() => cb(doc));
  }, [editor]);

  return (
    <div className="blocknote-editor-wrapper min-h-[400px] relative">
      {/* 협업 모드 로딩 오버레이 */}
      {!collabReady && (
        <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <div className="size-4 border-2 border-gray-300 border-t-primary-600 rounded-full animate-spin" />
            실시간 문서 동기화 중...
          </div>
        </div>
      )}
      <BlockNoteView
        editor={editor as any}
        editable={editable}
        theme="light"
        onChange={handleEditorChange}
      />
    </div>
  );
}

export { schema as documentEditorSchema };
